import Papa from 'papaparse'
import { DateTime } from 'luxon'
import type { RotationMember } from './RotationGrid'
import { WEEKDAYS } from './rotation-constants'

// Parses the team's rotation sheet: dates across a row, and the on-call
// person's name in the row directly beneath each date — the way these sheets
// are kept in spreadsheets:
//
//   Mon,Tue,Wed,Thu,Fri,Sat,Sun          <- optional weekday labels (ignored)
//   6/29/2026,6/30/2026,7/1/2026,...     <- date row
//   Joey,Mayer,Kedir,...                 <- who's on call that day
//
// Repeat date row + name row for each week. Extra sheet furniture is
// tolerated: title/preamble rows are skipped, a label cell at the start of a
// row (e.g. "Date", "North") is ignored, and dates may be ISO, US-style, or
// spreadsheet-style like "29-Jun" (year inferred nearest to today).
//
// In a name row, a named cell assigns the day, a blank cell explicitly clears
// it, and an unresolvable name surfaces as an error cell the user fixes in the
// grid. A fully blank name row is skipped (uploading the untouched template is
// a no-op rather than a mass clear). Days before today are skipped and counted
// — the calendar treats the past as read-only, so an upload can't rewrite it.

// The seven weekday columns a rotation sheet lays out, Mon-first.
const WEEKDAY_COLUMNS = WEEKDAYS

const WEEKDAY_NAMES = new Set([
  'mon',
  'monday',
  'tue',
  'tues',
  'tuesday',
  'wed',
  'wednesday',
  'thu',
  'thur',
  'thurs',
  'thursday',
  'fri',
  'friday',
  'sat',
  'saturday',
  'sun',
  'sunday',
])

export interface RotationCsvResult {
  /** date -> staffId (assign) or null (clear — cell was blank in the file). */
  edits: Record<string, string | null>
  /** date -> raw name that didn't resolve to a member ("did you mean" cells). */
  unknownByDate: Record<string, string>
  /** structural problems (no date rows, ambiguous name rows). */
  errors: string[]
  /** covered days before today, dropped — the past is read-only. */
  pastDaysSkipped: number
}

// Accept the formats a spreadsheet export plausibly produces. Year-less
// formats ("29-Jun") parse into the current year, then snap to the year that
// puts the date within six months of today (a December sheet read in January
// belongs to the year that just ended, not eleven months out).
function parseDateCell(raw: string, today: DateTime): DateTime | null {
  const value = raw.trim()
  if (!value) return null
  for (const format of ['M/d/yyyy', 'M/d/yy', 'd-MMM-yyyy']) {
    const dt = DateTime.fromFormat(value, format)
    if (dt.isValid) return dt
  }
  {
    const dt = DateTime.fromISO(value)
    if (dt.isValid) return dt
  }
  for (const format of ['d-MMM', 'MMM d']) {
    const dt = DateTime.fromFormat(value, format)
    if (dt.isValid) {
      if (dt < today.minus({ months: 6 })) return dt.plus({ years: 1 })
      if (dt > today.plus({ months: 6 })) return dt.minus({ years: 1 })
      return dt
    }
  }
  return null
}

// Starter sheet for the expected format: a weekday label row, then for each of
// the next `weeks` weeks a row of dates (Mon–Sun, beginning with the current
// week) above a blank row to fill in with names.
export function buildRotationCsvTemplate(todayIso: string, weeks = 4): string {
  const monday = DateTime.fromISO(todayIso).startOf('week')
  const lines = [WEEKDAY_COLUMNS.join(',')]
  for (let week = 0; week < weeks; week++) {
    const dates = Array.from({ length: WEEKDAY_COLUMNS.length }, (_, day) =>
      monday.plus({ weeks: week, days: day }).toFormat('M/d/yyyy'),
    )
    lines.push(dates.join(','))
    lines.push(','.repeat(WEEKDAY_COLUMNS.length - 1)) // blank name row
  }
  return lines.join('\n')
}

export function parseRotationCsv(
  csvText: string,
  members: RotationMember[],
  todayIso: string = DateTime.now().toISODate() ?? '',
): RotationCsvResult {
  const result: RotationCsvResult = {
    edits: {},
    unknownByDate: {},
    errors: [],
    pastDaysSkipped: 0,
  }
  // How many date rows we parsed — drives the "no date row" structural error.
  let weekCount = 0
  const today = DateTime.fromISO(todayIso)

  const parsed = Papa.parse<string[]>(csvText, { skipEmptyLines: true })

  const memberByName = new Map(
    members.map(member => [member.name.trim().toLowerCase(), member.id]),
  )

  // Per-column dates from the most recent date row, waiting for a name row.
  let pendingDates: (string | null)[] | null = null
  let pendingConsumed = false

  parsed.data.forEach((row, index) => {
    const rowNumber = index + 1
    const cells = row.map(cell => (cell ?? '').trim())
    const nonEmpty = cells.filter(Boolean)
    if (nonEmpty.length === 0) return

    // Date row: (almost) every filled cell is a date. One non-date cell is
    // tolerated so a leading label column ("Date") doesn't break detection.
    const dates = cells.map(cell => (cell ? parseDateCell(cell, today) : null))
    const dateCount = dates.filter(Boolean).length
    if (dateCount >= 2 && nonEmpty.length - dateCount <= 1) {
      pendingDates = dates.map(date => date?.toISODate() ?? null)
      pendingConsumed = false
      weekCount += 1
      return
    }

    // Weekday label row ("Mon,Tue,…" — possibly with a label cell): cosmetic.
    const weekdayCount = nonEmpty.filter(cell =>
      WEEKDAY_NAMES.has(cell.toLowerCase()),
    ).length
    if (weekdayCount >= 2 && nonEmpty.length - weekdayCount <= 1) return

    // Preamble before any date row (titles, "Week - 1" banners): skip.
    if (!pendingDates) return

    // Name row. Only one per date row — a second is ambiguous (which region /
    // which person?), so it blocks the upload rather than guessing.
    if (pendingConsumed) {
      result.errors.push(
        `Row ${rowNumber}: extra row of names — keep one name row under each date row`,
      )
      return
    }
    pendingConsumed = true
    pendingDates.forEach((date, column) => {
      if (!date) return // no date above this column (label column, blank)
      if (date < todayIso) {
        result.pastDaysSkipped += 1 // past days are read-only on the calendar
        return
      }
      const rawName = cells[column] ?? ''
      if (!rawName) {
        result.edits[date] = null // blank cell = clear the day
        return
      }
      const staffId = memberByName.get(rawName.toLowerCase())
      if (staffId) {
        result.edits[date] = staffId
      } else {
        result.unknownByDate[date] = rawName
      }
    })
  })

  if (weekCount === 0) {
    result.errors.push(
      "No date row found — put each day's date above the name of who's on call (see the template)",
    )
  }
  return result
}
